import { useContext, createContext, ReactNode, useState } from 'react';
import { EditAccountLimitsFormValues, ContractLimit } from '../../../types/limits';

interface ManageLimitsContextParams {
  accountLimits: EditAccountLimitsFormValues;
  contractLimits: ContractLimit[];
  setAccountLimits: (limits: EditAccountLimitsFormValues) => void;
  setContractLimits: (limits: ContractLimit[]) => void;
  removeAccountLimit: (key: keyof EditAccountLimitsFormValues) => void;
  removeContractLimit: (id: string) => void;
}

const DialogContext = createContext<ManageLimitsContextParams | undefined>(undefined);

export const useManageLimits = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useManageLimits must be used within a ManageLimitsProvider');
  }
  return context;
};

export const ManageLimitsProvider = (props: { children: ReactNode }) => {
  const defaultLimits: EditAccountLimitsFormValues = {
    singleTradeSizeLimit: '',
    dailyLossLimit: '',
    dailyLossLimitType: null,
    enforceDailyLossLimit: null,
    marginPercentage: '',
    enforceMarginSubsystem: null
  };

  const [accountLimits, setAccountLimits] = useState<EditAccountLimitsFormValues>({ ...defaultLimits });
  const [contractLimits, setContractLimits] = useState<ContractLimit[]>([]);

  const removeAccountLimit = (key: keyof EditAccountLimitsFormValues) => {
    const copyAccountLimits = { ...accountLimits };
    delete (copyAccountLimits as any)[key];
    const respreadLimits = { ...defaultLimits, ...copyAccountLimits };
    setAccountLimits(respreadLimits);
  };

  const removeContractLimit = (id: string) => {
    setContractLimits(contractLimits.filter((limit) => limit.contractId !== id));
  };

  return (
    <DialogContext.Provider value={{ accountLimits, contractLimits, setContractLimits, setAccountLimits, removeAccountLimit, removeContractLimit }}>
      {props.children}
    </DialogContext.Provider>
  );
};
